import React, { useState, useEffect, useCallback } from 'react';
import { CreditRecharge, CreditConsumption, LLMModelInfo, RechargeRequest } from '../types';
import { getCreditsBalance, getMyRecharges, getMyConsumptions, listLLMProvidersPublic, listActiveVideoModels, listActiveImageModels, listMyRechargeRequests, cancelRechargeRequest } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import EmptyState from '../components/EmptyState';
import { useToast } from '../components/Toast';
import { creditsToYuan } from '../utils/currency';
import { useTranslation } from 'react-i18next';
import { toI18nKeySegment } from '../utils/i18nKey';

// OPERATION_LABELS moved to i18n: profile.operationTypes.*

// TYPE_LABELS moved to i18n: profile.categoryTypes.*

function formatTime(s: string): string {
  if (!s) return '-';
  const d = new Date(s);
  return d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const PAGE_SIZE = 20;

type Tab = 'recharges' | 'consumptions' | 'requests';

const MyCredits: React.FC = () => {
  const { t } = useTranslation('profile');
  const [tab, setTab] = useState<Tab>('consumptions');
  const [balance, setBalance] = useState<number | null>(null);

  // Recharges state
  const [recharges, setRecharges] = useState<CreditRecharge[]>([]);
  const [rechargeTotal, setRechargeTotal] = useState(0);
  const [rechargePage, setRechargePage] = useState(1);
  const [rechargeLoading, setRechargeLoading] = useState(false);

  // Consumptions state
  const [consumptions, setConsumptions] = useState<CreditConsumption[]>([]);
  const [consumptionTotal, setConsumptionTotal] = useState(0);
  const [consumptionPage, setConsumptionPage] = useState(1);
  const [consumptionLoading, setConsumptionLoading] = useState(false);

  const [modelNames, setModelNames] = useState<Record<string, string>>({});
  const [initLoading, setInitLoading] = useState(true);

  const toast = useToast();
  const [requests, setRequests] = useState<RechargeRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  useEffect(() => {
    getCreditsBalance().then(r => setBalance(r.credits)).catch(() => {});
    // Build model ID → display name map
    const nameMap: Record<string, string> = {};
    Promise.all([
      listLLMProvidersPublic().then(providers => {
        for (const p of providers) {
          try {
            const models: LLMModelInfo[] = JSON.parse(p.models || '[]');
            for (const m of models) {
              if (m.displayName) nameMap[m.name] = m.displayName;
            }
          } catch {}
        }
      }).catch(() => {}),
      listActiveVideoModels().then(models => {
        for (const m of models) {
          if (m.displayName) nameMap[m.model] = m.displayName;
        }
      }).catch(() => {}),
      listActiveImageModels().then(models => {
        for (const m of models) {
          if (m.displayName) nameMap[m.model] = m.displayName;
        }
      }).catch(() => {}),
    ]).then(() => setModelNames(nameMap));
  }, []);

  const loadRecharges = useCallback((p: number) => {
    setRechargeLoading(true);
    getMyRecharges(p, PAGE_SIZE)
      .then(res => {
        setRecharges(res.items || []);
        setRechargeTotal(res.total);
        setRechargePage(p);
      })
      .catch(() => {})
      .finally(() => { setRechargeLoading(false); setInitLoading(false); });
  }, []);

  const loadConsumptions = useCallback((p: number) => {
    setConsumptionLoading(true);
    getMyConsumptions(p, PAGE_SIZE)
      .then(res => {
        setConsumptions(res.items || []);
        setConsumptionTotal(res.total);
        setConsumptionPage(p);
      })
      .catch(() => {})
      .finally(() => { setConsumptionLoading(false); setInitLoading(false); });
  }, []);

  const loadRequests = useCallback(() => {
    setRequestsLoading(true);
    listMyRechargeRequests(1, 50)
      .then(res => setRequests(res.items || []))
      .catch(() => {})
      .finally(() => { setRequestsLoading(false); setInitLoading(false); });
  }, []);

  const handleCancelRequest = async (id: string) => {
    try {
      await cancelRechargeRequest(id);
      toast.success(t('myCredits.cancelRequestSuccess'));
      loadRequests();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('myCredits.cancelRequestFailed'));
    }
  };

  useEffect(() => {
    if (tab === 'recharges') {
      loadRecharges(1);
    } else if (tab === 'requests') {
      loadRequests();
    } else {
      loadConsumptions(1);
    }
  }, [tab, loadRecharges, loadConsumptions, loadRequests]);

  if (initLoading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('myCredits.loading')}</div>
      </div>
    );
  }

  const rechargePages = Math.ceil(rechargeTotal / PAGE_SIZE);
  const consumptionPages = Math.ceil(consumptionTotal / PAGE_SIZE);

  return (
    <div className="container">
      <Breadcrumb items={[
        { label: t('myCredits.breadcrumbProjects'), to: '/projects' },
        { label: t('myCredits.breadcrumbTitle') },
      ]} />

      <div className="page-header">
        <h1 className="page-title">{t('myCredits.pageTitle')}</h1>
      </div>

      {/* Balance card */}
      {balance !== null && (
        <div className="card" style={{
          textAlign: 'center', padding: '1.25rem 1.5rem', marginBottom: '1.5rem',
          background: balance < 1000 ? 'var(--danger-light, rgba(239,68,68,0.08))' : 'var(--primary-light)',
          borderColor: balance < 1000 ? 'var(--danger)' : 'var(--primary)',
        }}>
          <span style={{ fontSize: '0.9rem', color: 'var(--text-2)', marginRight: '0.75rem' }}>{t('myCredits.currentBalance')}</span>
          <span style={{ fontSize: '2rem', fontWeight: 700, color: balance < 1000 ? 'var(--danger)' : 'var(--primary)' }}>
            ¥{creditsToYuan(balance)}
          </span>
          <span style={{ fontSize: '0.9rem', color: 'var(--text-2)', marginLeft: '0.25rem' }}>{t('myCredits.currencyUnit')}</span>
        </div>
      )}

      {/* Tab buttons */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          className={`btn ${tab === 'consumptions' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
          onClick={() => setTab('consumptions')}
        >
          {t('myCredits.tabConsumption')}
        </button>
        <button
          className={`btn ${tab === 'recharges' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
          onClick={() => setTab('recharges')}
        >
          {t('myCredits.tabAdjustment')}
        </button>
        <button
          className={`btn ${tab === 'requests' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
          onClick={() => setTab('requests')}
        >
          {t('myCredits.tabRequests')}
        </button>
      </div>

      {/* Consumptions tab */}
      {tab === 'consumptions' && (
        <div className="card">
          {consumptions.length > 0 ? (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)' }}>
                      <th style={thStyle}>{t('myCredits.thTime')}</th>
                      <th style={thStyle}>{t('myCredits.thProject')}</th>
                      <th style={thStyle}>{t('myCredits.thType')}</th>
                      <th style={thStyle}>{t('myCredits.thOperation')}</th>
                      <th style={thStyle}>{t('myCredits.thModel')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('myCredits.thAmount')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('myCredits.thBalance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consumptions.map(row => (
                      <tr key={row.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ ...tdStyle, color: 'var(--text-3)', fontSize: '0.8rem' }}>{formatTime(row.createdAt)}</td>
                        <td style={tdStyle}>{row.projectName || '-'}</td>
                        <td style={tdStyle}>
                          <span className="status-badge status-uploading" style={{ fontSize: '0.75rem' }}>
                            {t('categoryTypes.' + toI18nKeySegment(row.operationType), { defaultValue: row.operationType })}
                          </span>
                        </td>
                        <td style={tdStyle}>{t('operationTypes.' + toI18nKeySegment(row.operationName), { defaultValue: row.operationName })}</td>
                        <td style={{ ...tdStyle, color: 'var(--text-2)', fontSize: '0.8rem' }}>{modelNames[row.modelName] || row.modelName || '-'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500, color: 'var(--danger)' }}>-¥{creditsToYuan(row.amount)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-3)' }}>¥{creditsToYuan(row.balanceAfter)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {consumptionPages > 1 && (
                <Pagination page={consumptionPage} totalPages={consumptionPages} loading={consumptionLoading} onPageChange={loadConsumptions} />
              )}
            </>
          ) : (
            <EmptyState icon="&#128202;" description={t("myCredits.emptyConsumption")} />
          )}
        </div>
      )}

      {/* Recharges tab */}
      {tab === 'recharges' && (
        <div className="card">
          {recharges.length > 0 ? (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)' }}>
                      <th style={thStyle}>{t('myCredits.thAdjTime')}</th>
                      <th style={thStyle}>{t('myCredits.thAdjType')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('myCredits.thAdjAmount')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('myCredits.thAdjBalance')}</th>
                      <th style={thStyle}>{t('myCredits.thAdjNote')}</th>
                      <th style={thStyle}>{t('myCredits.thAdjOperator')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recharges.map(row => {
                      const isDeduct = row.type === 'deduct';
                      const sign = isDeduct ? '-' : '+';
                      const color = isDeduct ? 'var(--danger)' : 'var(--success)';
                      const badgeLabel = isDeduct ? t('myCredits.deduct') : t('myCredits.topup');
                      return (
                        <tr key={row.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ ...tdStyle, color: 'var(--text-3)', fontSize: '0.8rem' }}>{formatTime(row.createdAt)}</td>
                          <td style={tdStyle}>
                            <span style={{
                              background: color,
                              color: '#fff',
                              fontSize: '0.7rem',
                              padding: '1px 6px',
                              borderRadius: '10px',
                            }}>{badgeLabel}</span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500, color }}>{sign}¥{creditsToYuan(row.amount)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: row.balanceAfter < 0 ? 'var(--danger)' : 'var(--text-3)' }}>¥{creditsToYuan(row.balanceAfter)}</td>
                          <td style={tdStyle}>{row.remark || '-'}</td>
                          <td style={{ ...tdStyle, color: 'var(--text-2)' }}>{row.adminName || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {rechargePages > 1 && (
                <Pagination page={rechargePage} totalPages={rechargePages} loading={rechargeLoading} onPageChange={loadRecharges} />
              )}
            </>
          ) : (
            <EmptyState icon="&#128176;" description={t("myCredits.emptyAdjustment")} />
          )}
        </div>
      )}

      {/* 充值申请 tab */}
      {tab === 'requests' && (
        <div className="card">
          {requests.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={thStyle}>{t('myCredits.thTime')}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('myCredits.thReqAmount')}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('myCredits.thApprovedAmount')}</th>
                    <th style={thStyle}>{t('myCredits.thReqStatus')}</th>
                    <th style={thStyle}>{t('myCredits.thReqReviewer')}</th>
                    <th style={thStyle}>{t('myCredits.thReqNote')}</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ ...tdStyle, color: 'var(--text-3)', fontSize: '0.8rem' }}>{formatTime(r.createdAt)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>¥{creditsToYuan(r.requestedAmount)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{r.status === 'approved' ? `¥${creditsToYuan(r.approvedAmount)}` : '-'}</td>
                      <td style={tdStyle}>{t('myCredits.reqStatus.' + r.status, { defaultValue: r.status })}</td>
                      <td style={{ ...tdStyle, color: 'var(--text-2)' }}>{r.reviewerName || '-'}</td>
                      <td style={tdStyle}>{r.reviewRemark || r.remark || '-'}</td>
                      <td style={tdStyle}>
                        {r.status === 'pending' && (
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleCancelRequest(r.id)} disabled={requestsLoading}>
                            {t('myCredits.cancelRequest')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState icon="&#128221;" description={t('myCredits.emptyRequests')} />
          )}
        </div>
      )}
    </div>
  );
};

function Pagination({ page, totalPages, loading, onPageChange }: { page: number; totalPages: number; loading: boolean; onPageChange: (p: number) => void }) {
  const { t } = useTranslation('profile');
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      gap: '0.5rem', marginTop: '1rem', paddingTop: '1rem',
      borderTop: '1px solid var(--border)',
    }}>
      <button className="btn btn-ghost btn-sm" disabled={page <= 1 || loading} onClick={() => onPageChange(page - 1)}>
        {t('myCredits.prevPage')}
      </button>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-2)' }}>{page} / {totalPages}</span>
      <button className="btn btn-ghost btn-sm" disabled={page >= totalPages || loading} onClick={() => onPageChange(page + 1)}>
        {t('myCredits.nextPage')}
      </button>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  fontWeight: 600,
  color: 'var(--text-2)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  whiteSpace: 'nowrap',
};

export default MyCredits;
